/**
 * The Settings tab of a meeting series (#294): schedule, default attendees,
 * details, meeting template, attachments, and Archive. Designed for a phone
 * as much as a desktop - nothing depends on hover, and the cards stack under
 * 720px.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { Plus, X, FileText, ChevronDown, ChevronRight, Paperclip, Archive, Pencil, Check } from 'lucide-react';
import { DossierEditor } from '../editor';
import { apiGet, apiPatch } from '../../lib/api';
import { getInitials } from '../../lib/people';
import { getLocalToday } from '../../lib/dates';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { AddPersonRow } from '../AddPersonRow';
import { DismissBackdrop } from '../DismissBackdrop';
import { FileUpload } from '../FileUpload';
import { TimeSelect } from '../TimeSelect';
import { SaveStatus } from '../SaveStatus';

export interface SchedulingConfig {
  day_of_week?: number;       // 0=Sun..6=Sat (legacy)
  days_of_week?: number[];    // multi-day weekly
  day_of_month?: number;      // 1-31
  monthly_ordinal?: number;   // 1-4 or -1 (last)
  monthly_weekday?: number;   // 0=Sun..6=Sat
  default_time?: string;      // HH:MM
  default_duration?: number;  // minutes
}

export interface SettingsSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  template_content: string | null;
  scheduling_config: string | null;
  end_date: string | null;
  default_attendee_ids?: string[];
  scope_ids?: string[];
}

export interface Person { id: string; name: string; email: string | null }

interface Meeting { id: string; meeting_date: string; meeting_time: string | null }

export const CADENCES: Array<[string, string]> = [
  ['', 'No schedule'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['biweekly', 'Every two weeks'],
  ['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['adhoc', 'As needed'],
];
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Monday first, as the mockup shows.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DURATIONS: Array<[number, string]> = [[15, '15 min'], [30, '30 min'], [45, '45 min'], [60, '1 hour'], [90, '1.5 hours'], [120, '2 hours']];

export function parseSchedulingConfig(raw: string | null): SchedulingConfig {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

export function formatTime12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}

function formatDuration(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

function listWords(words: string[]): string {
  return words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

function shortDate(d: string): string {
  return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function longDate(d: string): string {
  return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The schedule in one sentence: "Every week on Friday at 10:00 AM, 30 min".
 * `fallbackTime` is the last meeting's time, which new meetings use when no
 * default is set.
 */
export function scheduleSentence(cadence: string | null, config: SchedulingConfig, endDate: string | null, fallbackTime?: string | null): string {
  if (!cadence) return 'No regular schedule';
  if (cadence === 'adhoc') return 'Meets as needed';
  const days = config.days_of_week?.length ? config.days_of_week : config.day_of_week !== undefined ? [config.day_of_week] : [];
  const ordered = WEEK_ORDER.filter((d) => days.includes(d));
  const onDays = ordered.length ? ` on ${listWords(ordered.map((d) => DAY_NAMES[d]))}` : '';
  let s: string;
  switch (cadence) {
    case 'daily': s = 'Every day'; break;
    case 'weekly': s = `Every week${onDays}`; break;
    case 'biweekly': s = `Every two weeks${onDays}`; break;
    case 'monthly':
    case 'quarterly': {
      s = cadence === 'monthly' ? 'Every month' : 'Every quarter';
      if (config.monthly_ordinal !== undefined && config.monthly_weekday !== undefined) {
        const which = config.monthly_ordinal === -1 ? 'last' : ordinal(config.monthly_ordinal);
        s += ` on the ${which} ${DAY_NAMES[config.monthly_weekday] ?? ''}`.trimEnd();
      } else if (config.day_of_month !== undefined) {
        s += ` on the ${ordinal(config.day_of_month)}`;
      }
      break;
    }
    default: s = cadence;
  }
  const time = config.default_time ?? fallbackTime;
  if (time) s += ` at ${formatTime12(time)}`;
  if (config.default_duration) s += `, ${formatDuration(config.default_duration)}`;
  if (endDate) s += ` - until ${longDate(endDate)}`;
  return s;
}

const card = 'rounded-xl border border-border p-4 sm:p-5';
const heading = 'text-xs font-semibold uppercase tracking-wide text-text-muted';
const field = 'px-3 py-2 bg-bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:border-accent';
const label = 'text-xs text-text-secondary';

export function SeriesSettings({ series, setSeries, people, setPeople, meetings, onScheduleSaved, onArchived }: {
  series: SettingsSeries;
  setSeries: (fn: (prev: SettingsSeries) => SettingsSeries) => void;
  people: Person[];
  setPeople: (fn: (prev: Person[]) => Person[]) => void;
  meetings: Meeting[];
  /** Called once a schedule change has saved, so dependants (the header's next date) can refresh. */
  onScheduleSaved: () => void;
  onArchived: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const config = parseSchedulingConfig(series.scheduling_config);
  const lastTime = [...meetings].filter((m) => m.meeting_time).sort((a, b) => b.meeting_date.localeCompare(a.meeting_date))[0]?.meeting_time ?? null;

  /** Optimistic PATCH: show the change now, put it back if the save fails. */
  const patch = useCallback(async (changes: Record<string, unknown>, local: Partial<SettingsSeries>, failMsg: string) => {
    let before: SettingsSeries | null = null;
    setSeries((prev) => { before = prev; return { ...prev, ...local }; });
    try {
      const saved = await apiPatch<Partial<SettingsSeries>>(`/meeting-series/${series.id}`, changes);
      if (saved && typeof saved === 'object') {
        setSeries((prev) => ({
          ...prev,
          ...(Array.isArray(saved.default_attendee_ids) ? { default_attendee_ids: saved.default_attendee_ids } : {}),
          ...(Array.isArray(saved.scope_ids) ? { scope_ids: saved.scope_ids } : {}),
        }));
      }
      return true;
    } catch {
      if (before) { const b: SettingsSeries = before; setSeries(() => b); }
      toast.error(failMsg);
      return false;
    }
  }, [series.id, setSeries, toast]);

  const saveConfig = async (update: Partial<SchedulingConfig>) => {
    const next: Record<string, unknown> = { ...config, ...update };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === null || next[k] === '') delete next[k];
    const str = Object.keys(next).length ? JSON.stringify(next) : null;
    if (await patch({ scheduling_config: next }, { scheduling_config: str }, 'Failed to update the schedule')) onScheduleSaved();
  };

  return (
    <div className="@container space-y-4" data-testid="series-settings">
      <div className="grid grid-cols-1 gap-4 @min-[720px]:grid-cols-2 items-start">
        <ScheduleCard
          series={series} config={config} lastTime={lastTime} meetings={meetings}
          onCadence={async (cadence) => { if (await patch({ cadence: cadence || null }, { cadence: cadence || null }, 'Failed to update how often it repeats')) onScheduleSaved(); }}
          onConfig={saveConfig}
          onEndDate={async (end) => { if (await patch({ end_date: end }, { end_date: end }, 'Failed to update the end date')) onScheduleSaved(); }}
        />
        <div className="space-y-4">
          <AttendeesCard
            ids={series.default_attendee_ids ?? []} people={people} setPeople={setPeople}
            onChange={(ids) => patch({ default_attendee_ids: ids }, { default_attendee_ids: ids }, 'Failed to update default attendees')}
          />
          <DetailsCard series={series} patch={patch} />
        </div>
      </div>
      <TemplateCard series={series} setSeries={setSeries} />
      <AttachmentsCard seriesId={series.id} />
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4 text-sm text-text-muted">
        <button
          type="button"
          onClick={async () => {
            if (!await confirm({ title: 'Archive this series?', message: 'It will be hidden from active views, and no meetings will be suggested until you unarchive it.', confirmLabel: 'Archive' })) return;
            try {
              await apiPatch(`/meeting-series/${series.id}/archive`, {});
              toast.success('Series archived');
              onArchived();
            } catch { toast.error('Failed to archive series'); }
          }}
          className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-hover"
        >
          <Archive size={14} /> Archive series
        </button>
        <span className="text-xs">Hides it everywhere; suggestions stop until it is unarchived.</span>
      </div>
    </div>
  );
}

function ScheduleCard({ series, config, lastTime, meetings, onCadence, onConfig, onEndDate }: {
  series: SettingsSeries;
  config: SchedulingConfig;
  lastTime: string | null;
  meetings: Meeting[];
  onCadence: (cadence: string) => void;
  onConfig: (update: Partial<SchedulingConfig>) => void;
  onEndDate: (end: string | null) => void;
}) {
  const cadence = series.cadence ?? '';
  const regular = !!cadence && cadence !== 'adhoc';
  const [pickingEnd, setPickingEnd] = useState(false);
  const ends = series.end_date || pickingEnd ? 'date' : 'never';
  const [upcoming, setUpcoming] = useState<string[]>([]);

  // Coming up: real meetings from today on, plus the series' next suggestions.
  useEffect(() => {
    if (!regular || series.end_date && series.end_date < getLocalToday()) { setUpcoming([]); return; }
    let current = true;
    apiGet<Array<{ date: string }>>(`/meeting-series/${series.id}/upcoming?count=3`)
      .then((r) => { if (current) setUpcoming(Array.isArray(r) ? r.map((d) => d.date).filter((d) => typeof d === 'string') : []); })
      .catch(() => { if (current) setUpcoming([]); });
    return () => { current = false; };
  }, [series.id, regular, series.scheduling_config, series.cadence, series.end_date, meetings.length]);
  const today = getLocalToday();
  const real = meetings.filter((m) => m.meeting_date >= today).map((m) => m.meeting_date);
  const coming = [...new Set([...real, ...upcoming])].sort().slice(0, 3).map((d) => ({ date: d, real: real.includes(d) }));

  const days = config.days_of_week ?? (config.day_of_week !== undefined ? [config.day_of_week] : []);
  const monthMode = config.monthly_ordinal !== undefined ? 'ordinal' : config.day_of_month !== undefined ? 'date' : '';

  return (
    <section aria-label="Schedule" className={`${card} space-y-4`}>
      <div className="space-y-1">
        <h2 className={heading}>Schedule</h2>
        <p className="text-base font-semibold text-text-primary" data-testid="schedule-sentence">
          {scheduleSentence(series.cadence, config, series.end_date, lastTime)}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 @min-[420px]:grid-cols-[6rem_1fr] @min-[420px]:items-center">
        <label htmlFor="series-cadence" className={label}>Repeats</label>
        <select id="series-cadence" value={cadence} onChange={(e) => onCadence(e.target.value)} className={`${field} @min-[420px]:w-48`}>
          {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        {(cadence === 'weekly' || cadence === 'biweekly') && (<>
          <span className={label} id="series-days">On</span>
          <div role="group" aria-labelledby="series-days" className="grid grid-cols-7 gap-1">
            {WEEK_ORDER.map((d) => {
              const on = days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  aria-label={DAY_NAMES[d]}
                  onClick={() => {
                    const next = on ? days.filter((x) => x !== d) : [...days, d].sort();
                    onConfig({ days_of_week: next.length ? next : undefined, day_of_week: undefined });
                  }}
                  className={`rounded-md border py-2 text-xs transition-colors ${on
                    ? 'border-accent bg-accent font-semibold text-white'
                    : 'border-border-hover text-text-secondary hover:border-accent hover:text-text-primary'}`}
                >
                  {DAY_SHORT[d]}
                </button>
              );
            })}
          </div>
        </>)}

        {(cadence === 'monthly' || cadence === 'quarterly') && (<>
          <label htmlFor="series-month-mode" className={label}>On</label>
          <div className="flex flex-wrap gap-2">
            <select
              id="series-month-mode"
              value={monthMode}
              onChange={(e) => {
                if (e.target.value === 'date') onConfig({ day_of_month: 1, monthly_ordinal: undefined, monthly_weekday: undefined });
                else if (e.target.value === 'ordinal') onConfig({ day_of_month: undefined, monthly_ordinal: 1, monthly_weekday: 1 });
                else onConfig({ day_of_month: undefined, monthly_ordinal: undefined, monthly_weekday: undefined });
              }}
              className={field}
            >
              <option value="">Same day as last time</option>
              <option value="date">A date</option>
              <option value="ordinal">A weekday</option>
            </select>
            {monthMode === 'date' && (
              <select aria-label="Day of the month" value={config.day_of_month ?? 1} onChange={(e) => onConfig({ day_of_month: Number(e.target.value) })} className={field}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
              </select>
            )}
            {monthMode === 'ordinal' && (<>
              <select aria-label="Which week" value={config.monthly_ordinal ?? 1} onChange={(e) => onConfig({ monthly_ordinal: Number(e.target.value) })} className={field}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{ordinal(n)}</option>)}
                <option value={-1}>Last</option>
              </select>
              <select aria-label="Weekday" value={config.monthly_weekday ?? 1} onChange={(e) => onConfig({ monthly_weekday: Number(e.target.value) })} className={field}>
                {WEEK_ORDER.map((d) => <option key={d} value={d}>{DAY_NAMES[d]}</option>)}
              </select>
            </>)}
          </div>
        </>)}

        {regular && (<>
          <span className={label}>At</span>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <TimeSelect
                value={config.default_time ?? ''}
                onChange={(v) => onConfig({ default_time: v || undefined })}
                className={`${field} w-32`}
                placeholder="Not set"
              />
              <span className="text-xs text-text-muted">for</span>
              <select
                aria-label="Duration"
                value={config.default_duration ?? ''}
                onChange={(e) => onConfig({ default_duration: e.target.value ? Number(e.target.value) : undefined })}
                className={`${field} w-32`}
              >
                <option value="">Not set</option>
                {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {!config.default_time && (
              <p className="text-[11px] text-text-muted" data-testid="time-hint">
                {lastTime ? `Not set - new meetings use the last meeting's time (${formatTime12(lastTime)}).` : 'Not set - new meetings start with no time.'}
              </p>
            )}
          </div>

          <span className={label} id="series-ends">Ends</span>
          <div className="flex flex-wrap items-center gap-2">
            <div role="group" aria-labelledby="series-ends" className="grid grid-cols-2 gap-1">
              {(['never', 'date'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={ends === v}
                  onClick={() => {
                    if (v === 'never') { setPickingEnd(false); if (series.end_date) onEndDate(null); }
                    else setPickingEnd(true);
                  }}
                  className={`rounded-md border px-3 py-2 text-xs transition-colors ${ends === v
                    ? 'border-accent bg-accent/15 text-text-primary'
                    : 'border-border-hover text-text-secondary hover:text-text-primary'}`}
                >
                  {v === 'never' ? 'Never' : 'On a date'}
                </button>
              ))}
            </div>
            {ends === 'date' && (
              <input
                type="date"
                aria-label="End date"
                value={series.end_date ?? ''}
                onChange={(e) => { const v = e.target.value || null; onEndDate(v); if (!v) setPickingEnd(false); }}
                className={field}
              />
            )}
          </div>
        </>)}
      </div>

      {regular && coming.length > 0 && (
        <div className="rounded-lg bg-bg-secondary px-3 py-2.5" data-testid="coming-up">
          <div className={`${heading} mb-1.5`}>Coming up</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {coming.map((c) => (
              <span key={c.date} className={`rounded-md px-2 py-1 ${c.real ? 'bg-bg-card text-text-primary' : 'border border-dashed border-border-hover text-text-secondary'}`}
                title={c.real ? 'Meeting created' : 'Suggested'}>
                {shortDate(c.date)}
              </span>
            ))}
          </div>
        </div>
      )}
      {!regular && (
        <p className="text-xs text-text-muted">Pick how often it repeats to get suggested meeting dates.</p>
      )}
    </section>
  );
}

function AttendeesCard({ ids, people, setPeople, onChange }: {
  ids: string[];
  people: Person[];
  setPeople: (fn: (prev: Person[]) => Person[]) => void;
  onChange: (ids: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!adding) return;
    inputRef.current?.focus();
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setAdding(false); setQuery(''); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [adding]);

  const add = (id: string) => { onChange([...ids, id]); setAdding(false); setQuery(''); };
  const available = people.filter((p) => !ids.includes(p.id) && (!query || p.name.toLowerCase().includes(query.toLowerCase())));
  const chosen = ids.map((id) => people.find((p) => p.id === id)).filter((p): p is Person => !!p);

  return (
    <section aria-label="Default attendees" className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={heading}>Default attendees</h2>
        <span className="text-[11px] text-text-muted">pre-checked on new meetings</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {chosen.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full bg-bg-card py-1 pl-1 pr-1 text-sm text-text-primary">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/20 text-[10px] font-semibold text-accent">{getInitials(p.name)}</span>
            <span className="max-w-[12rem] truncate">{p.name}</span>
            <button
              type="button"
              aria-label={`Remove ${p.name}`}
              onClick={() => onChange(ids.filter((x) => x !== p.id))}
              className="flex h-7 w-7 items-center justify-center rounded-full text-text-muted hover:bg-bg-hover hover:text-danger"
            >
              <X size={13} />
            </button>
          </span>
        ))}
        {/* #121: a tap outside only closes the picker - it does not also press
            a setting underneath. The picker is raised above the backdrop. */}
        {adding && <DismissBackdrop onDismiss={() => { setAdding(false); setQuery(''); }} />}
        <div className={`relative${adding ? ' z-30' : ''}`} ref={boxRef}>
          {adding ? (
            <>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setQuery(''); } }}
                placeholder="Search people..."
                className={`${field} w-56`}
              />
              {(available.length > 0 || query.trim()) && (
                <div className="absolute top-full left-0 z-20 mt-1 max-h-48 w-64 overflow-y-auto rounded-lg border border-border bg-bg-surface shadow-lg">
                  {available.map((p) => (
                    <button key={p.id} type="button" onClick={() => add(p.id)} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/20 text-[9px] font-semibold text-accent">{getInitials(p.name)}</span>
                      <span className="truncate text-sm text-text-primary">{p.name}</span>
                    </button>
                  ))}
                  <AddPersonRow
                    query={query}
                    people={people}
                    onCreated={(person) => { setPeople((prev) => [...prev, person as Person]); add(person.id); }}
                  />
                </div>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border-hover px-3 py-1.5 text-sm text-text-secondary hover:border-accent hover:text-text-primary"
            >
              <Plus size={13} /> Add person
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function DetailsCard({ series, patch }: {
  series: SettingsSeries;
  patch: (changes: Record<string, unknown>, local: Partial<SettingsSeries>, failMsg: string) => Promise<boolean>;
}) {
  const [name, setName] = useState(series.name);
  const [description, setDescription] = useState(series.description ?? '');
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => { setName(series.name); }, [series.name]);
  useEffect(() => { setDescription(series.description ?? ''); }, [series.description]);
  useEffect(() => {
    apiGet<Array<{ id: string; name: string }>>('/scopes')
      .then((r) => setProjects(Array.isArray(r) ? r : []))
      .catch(() => setProjects([]));
  }, []);
  const scopeIds = series.scope_ids ?? [];

  const saveName = () => {
    const v = name.trim();
    if (!v) { setName(series.name); return; }
    if (v !== series.name) void patch({ name: v }, { name: v }, 'Failed to rename the series');
  };
  const saveDescription = () => {
    const v = description.trim();
    if (v !== (series.description ?? '')) void patch({ description: v || null }, { description: v || null }, 'Failed to update the description');
  };

  return (
    <section aria-label="Details" className={`${card} space-y-3`}>
      <h2 className={heading}>Details</h2>
      <div className="grid grid-cols-1 gap-3 @min-[420px]:grid-cols-[6rem_1fr] @min-[420px]:items-center">
        <label htmlFor="series-name" className={label}>Name</label>
        <input
          id="series-name"
          value={name}
          maxLength={500}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setName(series.name); (e.target as HTMLInputElement).blur(); } }}
          className={field}
        />
        <label htmlFor="series-description" className={label}>Description</label>
        <textarea
          id="series-description"
          value={description}
          rows={2}
          placeholder="Add a description"
          onChange={(e) => setDescription(e.target.value)}
          onBlur={saveDescription}
          className={`${field} resize-y`}
        />
        <label htmlFor="series-project" className={label}>Project</label>
        <div className="space-y-1">
          <select
            id="series-project"
            value={scopeIds[0] ?? ''}
            onChange={(e) => {
              const ids = e.target.value ? [e.target.value, ...scopeIds.slice(1).filter((x) => x !== e.target.value)] : scopeIds.slice(1);
              void patch({ scope_ids: ids }, { scope_ids: ids }, 'Failed to update the project');
            }}
            className={`${field} w-full`}
          >
            <option value="">None</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {scopeIds.length > 1 && (
            <p className="text-[11px] text-text-muted">Also in {scopeIds.length - 1} more {scopeIds.length === 2 ? 'project' : 'projects'}.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function TemplateCard({ series, setSeries }: { series: SettingsSeries; setSeries: (fn: (prev: SettingsSeries) => SettingsSeries) => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [picker, setPicker] = useState<Array<{ id: string; name: string; description: string; content: object }> | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // The editor reads `content` only when it mounts (CLAUDE.md, "DossierEditor
  // needs key"), so a loaded template or a switch between preview and editing remounts it.
  const [version, setVersion] = useState(0);
  const content = (() => { try { return series.template_content ? JSON.parse(series.template_content) : null; } catch { return null; } })();
  const empty = !content || !Array.isArray(content.content) || content.content.length === 0;

  const onUpdate = useCallback(async (doc: JSONContent) => {
    const json = JSON.stringify(doc);
    setSeries((prev) => ({ ...prev, template_content: json }));
    setSaving(true);
    try {
      await apiPatch(`/meeting-series/${series.id}`, { template_content: json });
      setLastSaved(new Date().toLocaleTimeString());
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  }, [series.id, setSeries, toast]);

  const openPicker = () => {
    if (picker) { setPickerOpen((o) => !o); return; }
    apiGet<Array<{ id: string; name: string; description: string; content: object }>>('/meeting-templates')
      .then((t) => { setPicker(Array.isArray(t) ? t : []); setPickerOpen(true); })
      .catch(() => toast.error('Could not load templates'));
  };

  const load = async (tmpl: { name: string; content: object }) => {
    const json = JSON.stringify(tmpl.content);
    setPickerOpen(false);
    try {
      await apiPatch(`/meeting-series/${series.id}`, { template_content: json });
      setSeries((prev) => ({ ...prev, template_content: json }));
      setVersion((v) => v + 1);
      toast.success(`Loaded "${tmpl.name}" template`);
    } catch {
      toast.error('Failed to load template');
    }
  };

  return (
    <section aria-label="Meeting template" className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={heading}>Meeting template</h2>
        <div className="flex items-center gap-3 text-sm">
          {editing && <SaveStatus lastSaved={lastSaved} saving={saving} />}
          <div className="relative">
            <button type="button" onClick={openPicker} className="flex items-center gap-1 text-accent hover:text-accent-hover">
              <FileText size={13} /> Load a template
            </button>
            {pickerOpen && picker && picker.filter((t) => t.id !== 'blank').length > 0 && (
              <div className="absolute right-0 top-7 z-20 w-60 rounded-lg border border-border bg-bg-surface py-1 shadow-lg">
                {picker.filter((t) => t.id !== 'blank').map((t) => (
                  <button key={t.id} type="button" onClick={() => void load(t)} className="block w-full px-3 py-2 text-left hover:bg-bg-hover">
                    <span className="block text-xs font-medium text-text-primary">{t.name}</span>
                    <span className="block text-[11px] text-text-muted">{t.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setEditing((e) => !e); setVersion((v) => v + 1); }}
            className="flex items-center gap-1 text-accent hover:text-accent-hover"
          >
            {editing ? <><Check size={13} /> Done</> : <><Pencil size={13} /> Edit</>}
          </button>
        </div>
      </div>
      {editing ? (
        <div className="template-editor">
          <DossierEditor
            key={`edit-${version}`}
            content={content}
            onUpdate={onUpdate}
            editable
            placeholder="Set up a template for new meetings (e.g., agenda sections, standard topics)..."
          />
        </div>
      ) : empty ? (
        <p className="rounded-lg bg-bg-secondary px-4 py-3 text-sm text-text-muted" data-testid="template-empty">
          No template yet. New meetings start blank - Edit to write one, or load a ready-made one.
        </p>
      ) : (
        <div
          className="relative max-h-48 overflow-hidden rounded-lg bg-bg-secondary [mask-image:linear-gradient(to_bottom,#000_70%,transparent)]"
          data-testid="template-preview"
        >
          <DossierEditor key={`view-${version}`} content={content} editable={false} onUpdate={() => {}} />
        </div>
      )}
      <p className="text-[11px] text-text-muted">New meetings in this series start with this.</p>
    </section>
  );
}

function AttachmentsCard({ seriesId }: { seriesId: string }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  return (
    <section aria-label="Attachments" className="rounded-xl border border-border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-bg-hover rounded-xl"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Paperclip size={14} className="text-text-muted" />
        <span className={`${heading} flex-1`}>Attachments</span>
        {count !== null && <span className="text-xs text-text-muted">{count} {count === 1 ? 'file' : 'files'}</span>}
      </button>
      {/* Mounted while closed so the count is known; [hidden] keeps it out of sight. */}
      <div hidden={!open} className="border-t border-border p-4">
        <p className="mb-3 text-xs text-text-muted">Files shared across all meetings in this series.</p>
        <FileUpload parentType="meeting_series" parentId={seriesId} onCountChange={setCount} />
      </div>
    </section>
  );
}
